import React, { useMemo, memo, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import parsePhoneNumber, { getCountryCallingCode } from 'libphonenumber-js';

import Select from 'components/Select';
import ErrorMessage from 'components/ErrorMessage';
import { OptionWithFlag, ValueWithFlag } from 'utils/customComponents';
import './style.css';
import countries from 'utils/countries.json';
import { DEFAULT_COUNTRY } from 'utils/constants';

const PhoneInput = ({
  testId,
  label,
  name,
  error,
  countryFieldName,
  defaultCountry,
  defaultPhoneNumber,
  control,
  inputBelowLabel,
  labelClassName,
  className,
  inputWrapperClassName,
  ...rest
}) => {
  const [country, setCountry] = useState(defaultCountry);
  const [phoneNumber, setPhoneNumber] = useState(defaultPhoneNumber);
  const [parsedNumber, setParsedNumber] = useState('');

  useEffect(() => {
    if (phoneNumber.length > 3) {
      const parsed = parsePhoneNumber(phoneNumber, country.code);
      setParsedNumber(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedNumber = useMemo(
    () =>
      phoneNumber.length > 3 && !!parsedNumber
        ? parsedNumber?.format('NATIONAL', {
            nationalPrefix: false,
          })
        : phoneNumber,
    [parsedNumber, phoneNumber]
  );

  return (
    <div data-testid={testId}>
      <div
        className={
          inputBelowLabel
            ? 'block mb-4 lg:mb-2 max-h-20'
            : 'flex flex-1 flex-col lg:items-center mb-4 lg:flex-row'
        }
      >
        <label
          htmlFor={name}
          className={`label ${
            inputBelowLabel
              ? 'text-opacity-80'
              : `pb-1 lg:pb-0 ${labelClassName}`
          }`}
        >
          {label}
        </label>
        <div className={`relative ${className}`}>
          <div
            className={`flex flex-row phone-wrapper ${inputWrapperClassName} ${
              !!error ? 'error' : ''
            }`}
            data-testid="input-wrapper"
          >
            <Controller
              name={countryFieldName}
              control={control}
              defaultValue={defaultCountry}
              render={({ field: { onChange, ref, ...other } }) => (
                <Select
                  {...other}
                  {...rest}
                  id="phone-select"
                  name={countryFieldName}
                  options={countries}
                  components={{
                    DropdownIndicator: () => null,
                    Option: OptionWithFlag,
                    SingleValue: ValueWithFlag,
                  }}
                  onChange={(val) => {
                    const dial_code =
                      `+${getCountryCallingCode(val.code)}` || val.dial_code;
                    if (phoneNumber.length > 3) {
                      const parsed = parsePhoneNumber(phoneNumber, val.code);
                      setParsedNumber(parsed);
                    }
                    setCountry({ ...val, dial_code });
                    onChange({ ...val, dial_code });
                  }}
                  defaultValue={defaultCountry}
                  getOptionLabel={(option) => option.dial_code}
                  getOptionValue={(option) => option.name}
                  classNamePrefix="phone"
                  className="phone-input"
                  hasError={!!error}
                />
              )}
            />
            <Controller
              name={name}
              control={control}
              defaultValue={formattedNumber}
              render={({ field: { onChange, value, ...other } }) => (
                <input
                  {...other}
                  value={formattedNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    const parsed = parsePhoneNumber(
                      e.target.value,
                      country.code
                    );
                    setParsedNumber(parsed);
                    onChange(parsed?.nationalNumber);
                  }}
                  name={name}
                  id={name}
                  type="text"
                  data-testid="phone-number-input"
                  className={`input rounded-r w-full ${!!error ? 'error' : ''}`}
                />
              )}
            />
          </div>
          {!!error && !inputBelowLabel && (
            <ErrorMessage message={error} className="mt-1" />
          )}
        </div>
      </div>
      {!!error && inputBelowLabel && (
        <ErrorMessage message={error} className="-mt-3 lg:-mt-1" />
      )}
    </div>
  );
};

PhoneInput.propTypes = {
  testId: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  control: PropTypes.shape({}),
  error: PropTypes.string,
  required: PropTypes.bool,
  defaultPhoneNumber: PropTypes.string,
  defaultCountry: PropTypes.shape({}),
  countryFieldName: PropTypes.string,
  labelClassName: PropTypes.string,
  className: PropTypes.string,
  inputBelowLabel: PropTypes.bool,
  inputWrapperClassName: PropTypes.string,
};
PhoneInput.defaultProps = {
  testId: 'phone-input',
  label: '',
  name: 'phone-input',
  control: {},
  error: null,
  required: true,
  defaultPhoneNumber: '',
  defaultCountry: DEFAULT_COUNTRY,
  countryFieldName: 'phone_country',
  labelClassName: 'lg:w-4/12',
  className: '',
  inputBelowLabel: true,
  inputWrapperClassName: '',
};

export default memo(PhoneInput);
